import { Injectable } from '@angular/core';
import { Http } from '@angular/http';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/operator/map';
import 'rxjs/add/observable/forkJoin';
import { Config } from 'ionic-angular';

import { EventModel } from '../../app/models/event-model';
import { EventCodeModel } from '../../app/models/eventcode-model';
import { SubEventCodeModel } from '../../app/models/subeventcode-model';
import { GuestModel } from '../../app/models/guest-model';
import { GuestGroupModel } from '../../app/models/guestgroup-model';
import { QuestionnaireResponseModel } from '../../app/models/questionnaireresponse-model';

import { GuestService } from '../../providers/guest-service/guest-service';

@Injectable()
export class RestGuestService implements GuestService {
  private currentGuestGroup: GuestGroupModel;
  private currentGuest: GuestModel;

  private serverUrl : string;

  constructor(private http: Http, configuration: Config) {
    this.eraseLoggedInGuest();
    this.serverUrl = configuration.get('backendUrl');
  }

  public isLoggedIn() : boolean {
    return this.currentGuest != null;
  }

  public loginGuest(event: EventModel, firstName: string, lastName: string) : Observable<boolean> {
    this.validateCredentials(event, firstName, lastName);
    let validatedFirstName = firstName.trim();
    let validatedLastName = lastName.trim();
    return Observable.create(observer => {
      this.http.get(this.serverUrl+'/api/'+event.code.value+'/guestsByGroup')
        .map(res => {
          let g = res.json().find(d => d.guests.find(remoteGuest => (remoteGuest.firstName.toUpperCase() === validatedFirstName.toUpperCase()) && (remoteGuest.lastName.toUpperCase() === validatedLastName.toUpperCase())) !== undefined);
          if(g === undefined) {
            observer.next(false);
            observer.complete();
            return undefined;
          } else {
            return g._id;
          }
        })
        .catch(error => {
          console.error(error);
          observer.next(error);
          return this.handleError(error);
        })
        .toPromise()
        .then(groupName => {
          //console.log(event.code);
          //console.log(groupName);
          if(groupName !== undefined) {
            this.getGuestsInGroup(event.code, groupName)
              .subscribe(data => {
                  //console.log(data);
                  let matchingGuest = data.find(guest => guest.isIdentifiedBy(validatedFirstName, validatedLastName));
                  this.selectGuest(matchingGuest, data);
                  let successfulLogin = this.isLoggedIn();
                  if(successfulLogin) {
                    this.http.put(this.serverUrl+'/api/'+event.code.value+'/guestLogIn/'+matchingGuest.getId(), "{id:"+matchingGuest.getId()+"}")
                      .catch(err => {
                        console.error('cannot save login time: '+err);
                        return this.handleError(err);
                      })
                      .subscribe(dd => {});
                  }
                  observer.next(successfulLogin);
                  observer.complete();
                },
                error => {
                  this.handleError(error);
                  observer.next(error);
                }
              );
          }
      });
    });
  }

  getGuestName(event: EventModel, guestKey: string) : Observable<string> {
    if(guestKey === undefined) {
      return Observable.create(observer => {
        observer.next("");
        observer.complete();
      });
    } else {
      return this.http.get(this.serverUrl+'/api/'+event.code.value+'/guest/'+guestKey.trim())
              .map(res => this.adaptFromRemoteGuest(event.code, res.json()).getFullName())
              .catch(this.handleError);
    }
  }

  private getGuestsInGroup(eventCode: EventCodeModel, groupName : string) : Observable<Array<GuestModel>> {
    if(groupName === undefined) {
      return Observable.create(observer => {
        observer.next(new Array<GuestModel>());
        observer.complete();
      });
    } else {
      return this.http.get(this.serverUrl+'/api/'+eventCode.value+'/guestsIn/'+groupName.trim())
              .map(res => res.json().map(guest => this.adaptFromRemoteGuest(eventCode, guest)))
              .catch(this.handleError);
    }
  }

  private selectGuest(guest : GuestModel, data: Array<GuestModel>) {
    if(guest === undefined) {
        console.warn("Guest not found.");
        this.eraseLoggedInGuest();
    } else {
      this.currentGuest = guest;
      let currentGroupName = this.currentGuest.getGroupName();
      this.currentGuestGroup = new GuestGroupModel(currentGroupName, data.filter(g => g.isInGroup(currentGroupName)));
    }
  }

  private eraseLoggedInGuest() {
    this.currentGuest = null;
    this.currentGuestGroup = null;
  }

  private validateCredentials(event: EventModel, firstName: string, lastName: string) {
    if (event == null || event.code == null || event.code.value == null || firstName == null || firstName === undefined || lastName == null || lastName === undefined) {
      return Observable.throw("Veuillez entrer des informations de connexion valides.");//TODO: i18n
    }
  }

  public logout() : Observable<boolean> {
    return Observable.create(observer => {
      this.eraseLoggedInGuest();
      observer.next(this.isLoggedIn());
      observer.complete();
    });
  }

  public getCurrentGuestGroup() : GuestGroupModel {
    return this.currentGuestGroup;
  }

  public getCurrentGuest() : GuestModel {
    return this.currentGuest;
  }

  private adaptFromRemoteGuest(eventCode : EventCodeModel, guest: any) : GuestModel{
    return new GuestModel(guest.key, guest.firstName, guest.lastName, guest.email, guest.groupName, eventCode, guest.nickName, guest.phone, guest.isCompanion, this.adaptFromRemoteSubEventDetails(guest.subEventDetails), this.adaptFromRemoteTopicVisits(guest.topicVisits), guest.guestbookVisit);
	}

  private adaptFromRemoteTopicVisits(visits: any) : Map<string,Date> {
    let result = new Map<string,Date>();
    visits.forEach(v => result.set(v.topicKey, v.lastVisitDate));
    return result;
  }

  private adaptFromRemoteSubEventDetails(details: any) : Map<string,QuestionnaireResponseModel> {
    let result = new Map<string,QuestionnaireResponseModel>();
    details.forEach(d => d.isInvited === true ? result.set(d.subEventCode, new QuestionnaireResponseModel(d.isComing, this.adaptFromRemoteQuestionnaire(d.questions))) : result.set(d.subEventCode, undefined));
    return result;
  }

  private adaptFromRemoteQuestionnaire(questions: any) : Map<string,any> {
    let result = new Map<string,any>();
    questions.forEach(q => result.set(q.questionCode, q.answer));
    return result;
  }

  private adaptToRemoteGuest(event: EventModel, guest: GuestModel) : any {
    let subEventDetails = [];
    let nbReplies= 0;
    for(let subEvent of event.subEvents) {
      let questions = [];//TODO: fill in questions from subevent
      for(let question of subEvent.questionnaire) {
        let code = question.code;
        questions.push({
          questionCode: code,
          answer: guest.getResponseFor(subEvent, code)
        });
      }
      let isInvited = guest.isInvitedTo(subEvent);
      let isComing = guest.isComingTo(subEvent);
      if(isComing !== undefined && isInvited) {
        nbReplies++;
      }
      subEventDetails.push({
        subEventCode: subEvent.code.value,
        isInvited: isInvited,
        isComing: isComing,
        questions: questions
      });
    }

    let topicVisits = [];
    guest.getVisitedTopics().forEach(v => topicVisits.push({topicKey: v, lastVisitDate: guest.getLastVisitDateForTopic(v)}));
    let dbGuest = {
      key: guest.getId(),
      eventCode: guest.getEventCode().value,
      firstName: guest.getFirstName(),
      lastName: guest.getLastName(),
      nickName: guest.getNickName(),
      groupName: guest.getGroupName(),
      email: guest.getEmail(),
      phone: guest.getPhone(),
      hasReplied: nbReplies > 0,
      isCompanion: guest.isAPlusOne(),
      subEventDetails: subEventDetails,
      topicVisits: topicVisits,
	  guestbookVisit: guest.getLastVisitDateForGuestbook()
    };
    return dbGuest;
	}

  private handleError(error) {
    console.error(error);
    return Observable.throw((error.status === 404 ? error.text() : error.json().error) || 'Server error while reading guest data');
  }

  public save(guests : Array<GuestModel>, event: EventModel, groupName: string) : Observable<boolean> {
    return Observable.create(observer => {
      let errorMessage = "";
	  let requests = [];
	  for(let guest of guests) {
      let matchingGuests = this.currentGuestGroup.guests.filter(g => g.getId() === guest.getId());
      if(matchingGuests.length === 0) {
        errorMessage += "Unable to find an existing guest with id "+guest.getId()+".\n";
      } else {
        requests.push(
          this.http.put(this.serverUrl+'/api/'+event.code.value+'/guestReply/'+guest.getId(), this.adaptToRemoteGuest(event, guest))
            .catch(error => {
              console.error(error);
              errorMessage += error;
              return this.handleError(errorMessage);
            })
        );
      }
	  }
    Observable.forkJoin(requests)
      .catch(error => {
        errorMessage += error;
        return this.handleError(errorMessage);
      })
      .toPromise()
      .then(joined => {
        if(groupName !== undefined) {
          this.getGuestsInGroup(event.code, groupName)
            .subscribe(data => {
              let loggedInGuest = data.find(g => g.getId() == this.currentGuest.getId());
              this.selectGuest(loggedInGuest, data);
              if(errorMessage.length > 0) {
                return this.handleError(errorMessage);
              } else {
                console.log("Saved all modified guests");
                observer.next(true);
                observer.complete();
              }
            }, error => {
              observer.next(error);
              return this.handleError(error);
            });
        }
      });
    });
  }
}



// WEBPACK FOOTER //
// ./src/providers/guest-service/rest-guest-service.ts